<template>
  <div class="nx-entity-form-generator">
    <template v-if="draft">
      <nx-form-generator
        :basic-advanced-enabled="useAdvancedBasicEditor"
        :entity-id="id"
        :entity="entity"
        :entity-relative-version-id="relativeVersionId"
        :config="config"
        :editor-fields-config="editorFieldsConfig"
        :disabled="isDisabled"
        :show-tabs="true"
        :form-value="draft"
        :affix-tabs="!modal"
        :form-scope-component="formScopeComponent"
        @errors="updateFormErrors"
        @fields="updateFormFieldsFlags"
        @form="updateFormFlags"
        @input="updateField"
        @trigger-population="handleTriggerPopulation"
        ref="form"
      ></nx-form-generator>
    </template>
    <div v-if="config.length === 0">
      <v-alert type="error" :value="true">
        No form config found for <strong>{{ type }}</strong>
      </v-alert>
    </div>

    <nx-server-blocked-message :active="serverBlocked" :message="serverBlockedMessage"></nx-server-blocked-message>
  </div>
</template>

<script>
import NxFormGenerator from '@/modules/form/FormGenerator';
import NxServerBlockedMessage from '../../content/editor/components/ServerBlockedMessage';
import FormChannelScopeGenerator from '@/modules/content/editor/form/FormChannelScopeGenerator';
import { forms } from '@/modules/form/config';
import TriggerPopulation from '@/modules/entity/mixins/entity-trigger-population';
import get from 'lodash/get';
import { mapGetters } from 'vuex';

export default {
  name: 'nx-entity-form-generator',
  mixins: [TriggerPopulation],
  components: {
    NxFormGenerator,
    NxServerBlockedMessage,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    relativeVersionId: {
      type: Number,
    },
    type: {
      type: String,
      required: true,
    },
    draft: {
      type: Object,
      required: true,
    },
    channel: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    // Custom fields config in the entity editor api response (fields only for this entity)
    editorFieldsConfig: {
      type: Object,
    },
    serverBlockedMessage: {
      type: String,
    },
    modal: {
      type: Boolean,
      default: false,
    },
  },
  provide() {
    return {
      $wizardContext: {
        id: this.id,
        relativeVersionId: this.relativeVersionId,
        type: this.type,
        draft: this.draft,
      },
    };
  },
  computed: {
    ...mapGetters([
      'features',
    ]),
    entity() {
      return this.type;
    },
    entityId() {
      return this.id;
    },
    /**
     * The form configuration for the given entity and channel, as defined in the CMS.
     * @return {[]|null}
     */
    cmsFormConfig() {
      if (this.channel) {
        return get(forms, `${this.type}.${this.channel}`, null);
      }
      return get(forms, this.type, null);
    },

    /**
     * The form is configured for the advanced/basic editor
     * @return {*|boolean}
     */
    useAdvancedBasicEditor() {
      return this.entityConfig.advancedBasicEditor || false;
    },

    isDisabled() {
      return this.disabled || this.serverBlocked;
    },
    /**
     * The entity configuration, given by the server.
     * @return {*}
     */
    entityConfig() {
      return this.$store.getters.getEntityConfig(this.type);
    },

    /**
     * The entity form configuration. If one is given by the server, it will be returned. Else,
     * returns the cms configuration, or null if that's not defined either.
     * @return {[]|null}
     */
    config() {
      if (this.serverFormConfig !== null) {
        return this.serverFormConfig;
      }
      if (this.cmsFormConfig !== null) {
        return this.cmsFormConfig;
      }

      console.error(`There is no configuration defined to generate the content form. Content type: ${this.type}. Channel: ${this.channel}.`);
      return [];
    },

    /**
     * The form configuration for the given entity and channel, as defined in the server.
     * @return {[]|null}
     */
    serverFormConfig() {
      if (!this.channel) {
        return get(this.entityConfig, 'form', null);
      }
      if (this.channel.includes('translation')) {
        return get(this.entityConfig, 'channels.translation.form', null);
      }
      return get(this.entityConfig, `channels.${this.channel}.form`, null);
    },

    serverBlocked() {
      return !!this.serverBlockedMessage;
    },

    formScopeComponent() {
      if (!this.channel) {
        return null;
      }
      const isSecondaryChannel = get(this.entityConfig, `channels.${this.channel}.secondaryChannel`, false);
      if (isSecondaryChannel) {
        return FormChannelScopeGenerator;
      }
      return null;
    },
  },
  methods: {
    updateField(field, value) {
      this.$emit('update:field', field, value);
    },
    updateFormFlags(flags) {
      this.$emit('update:form-flags', flags);
    },
    updateFormErrors(errors) {
      this.$emit('update:form-errors', errors);
    },
    updateFormFieldsFlags(fields) {
      this.$emit('update:form-fields-flags', fields);
    },
    resetFields() {
      if (this.$refs.form) {
        this.$refs.form.resetFields();
      }
    },
    validateAndShowErrors() {
      if (this.$refs.form) {
        this.$refs.form.validateAndShowErrors();
      }
    },
    validateForPublishAndShowErrors() {
      if (this.$refs.form) {
        this.$refs.form.validateForPublishAndShowErrors();
      }
    },
    async handleTriggerPopulation({ fieldConfig, field, value }) {
      const population = await this.triggerPopulation({ fieldConfig, field, value });
      Object.entries(population).forEach(([key, val]) => {
        this.updateField(key, val);
      });
    },
  },
};
</script>
